import { useCallback, useEffect, useState } from 'react'

import { type StorageKey, type StorageValue } from './schemas'
import { getStorage, setStorage } from './storage'

// Overload: With default → non-nullable
export function useStorage<K extends StorageKey>(
	key: K,
	defaultValue: StorageValue<K>,
): [StorageValue<K>, (value: StorageValue<K>) => void]
// Overload: No default, or one that may be absent → nullable
export function useStorage<K extends StorageKey>(
	key: K,
	defaultValue?: StorageValue<K>,
): [StorageValue<K> | null, (value: StorageValue<K>) => void]
/**
 * React hook for type-safe localStorage with reactive updates
 */
export function useStorage<K extends StorageKey>(
	key: K,
	defaultValue?: StorageValue<K>,
): [StorageValue<K> | null, (value: StorageValue<K>) => void] {
	const [storedValue, setStoredValue] = useState<StorageValue<K> | null>(() =>
		defaultValue !== undefined
			? getStorage(key, defaultValue)
			: getStorage(key),
	)

	useEffect(() => {
		const handleStorageChange = (e: StorageEvent) => {
			if (e.key === key) {
				setStoredValue(
					defaultValue !== undefined
						? getStorage(key, defaultValue)
						: getStorage(key),
				)
			}
		}

		window.addEventListener('storage', handleStorageChange)
		return () => window.removeEventListener('storage', handleStorageChange)
	}, [key, defaultValue])

	const setValue = useCallback(
		(value: StorageValue<K>) => {
			setStoredValue(value)
			setStorage(key, value)
		},
		[key],
	)

	return [storedValue, setValue]
}

// Overload: No default → nullable
export function useStorageField<
	K extends StorageKey,
	F extends keyof StorageValue<K>,
>(
	key: K,
	field: F,
): [StorageValue<K>[F] | null, (value: StorageValue<K>[F]) => void]
// Overload: With default → non-nullable
export function useStorageField<
	K extends StorageKey,
	F extends keyof StorageValue<K>,
>(
	key: K,
	field: F,
	defaultStorageValue: StorageValue<K>,
): [StorageValue<K>[F], (value: StorageValue<K>[F]) => void]
/**
 * React hook for a specific field within a storage object
 */
export function useStorageField<
	K extends StorageKey,
	F extends keyof StorageValue<K>,
>(
	key: K,
	field: F,
	defaultStorageValue?: StorageValue<K>,
): [StorageValue<K>[F] | null, (value: StorageValue<K>[F]) => void] {
	// One call, whatever the default: a hook behind a ternary is a
	// conditional call, and `useStorage` already reads an absent default.
	const [storage, setStorageValue] = useStorage(key, defaultStorageValue)

	const fieldValue = storage?.[field] ?? null

	const setFieldValue = useCallback(
		(value: StorageValue<K>[F]) => {
			if (storage) {
				setStorageValue({ ...storage, [field]: value })
			}
		},
		[storage, field, setStorageValue],
	)

	return [fieldValue, setFieldValue]
}
